import Button from '@components/elements/button/button';
import CircleButton from '@components/elements/circle-button/circle-button';
import CopyArea from '@components/elements/copy-area/copy-area';
import DrawerModal from '@components/elements/drawer-modal/drawer-modal';
import Image from '@components/elements/image/image';
import QRCode from '@components/elements/qr-code/qr-code';
import CrossIcon from '@components/icons/cross-icon';
import { v2_Coupon, v2_useCoupon2 } from '@crocodille/common';
import { isSome, noop } from '@crocodille/util';
import { Dialog } from '@headlessui/react';
import { formatTime } from '@utils/format';
import { parseJSON } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import styles from './coupon-modal.module.scss';

interface CouponModalProps {
  readonly coupon: v2_Coupon | null;
  onClose?(): void;
}

type ApplyCouponState = 'select' | 'qr' | 'code';

const CouponModal: React.FC<CouponModalProps> = ({
  coupon: couponProp,
  onClose,
}) => {
  const {
    t,
    i18n: { language: locale },
  } = useTranslation();

  const { data: coupon, claim } = v2_useCoupon2(couponProp?.id);

  const [applyCouponState, setApplyCouponState] = useState(
    null as ApplyCouponState | null,
  );

  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (couponProp?.id == null) {
      return;
    }

    const interval = setInterval(() => {
      setNow(new Date());
    }, 60 * 1000);

    return () => {
      clearInterval(interval);
    };
  }, [couponProp?.id]);

  const validTo = useMemo(
    () => (coupon?.validTo != null ? parseJSON(coupon?.validTo) : null),
    [coupon?.validTo],
  );

  const personalizedCouponValid = useMemo(
    () => validTo != null && validTo > now,
    [validTo, now],
  );

  const [couponCodeRevealed, setCouponCodeRevealed] = useState(false);

  useEffect(() => {
    if (couponProp?.id != null) {
      setCouponCodeRevealed(false);
    }
  }, [couponProp?.id]);

  return (
    <>
      <Dialog open={isSome(coupon)} onClose={onClose ?? noop}>
        <div className={styles.backdrop}></div>
        <Dialog.Panel className={styles.panel}>
          <div className={styles.cover}>
            <Image
              src={coupon?.imageDetailPath}
              layout="fill"
              objectFit="cover"
              alt={coupon?.name}
            />
          </div>
          <div className={styles.details}>
            <CircleButton
              label={t('modal.close')}
              className={styles.closeButton}
              onClick={onClose}
              hiddenLabel
              inverse
            >
              <CrossIcon className={styles.closeIcon} />
            </CircleButton>
            <Dialog.Title className={styles.name}>
              {coupon?.name ?? ''}
            </Dialog.Title>
            <p className={styles.discount}>
              {[coupon?.discountText, coupon?.discountText2]
                .filter(isSome)
                .join(' ')}
            </p>
            <p
              className={styles.description}
              dangerouslySetInnerHTML={{
                __html: coupon?.fullDescriptionHtml ?? '',
              }}
            ></p>
            <>
              {isSome(coupon?.code) ||
              isSome(coupon?.qrCode) ||
              coupon?.personalized ? (
                <Button
                  round
                  size="large"
                  className={styles.selectButton}
                  display="block"
                  onClick={async () => {
                    if (coupon?.personalized && !personalizedCouponValid) {
                      await claim();
                    }

                    setApplyCouponState('select');
                  }}
                >
                  {t('coupons.apply')}
                </Button>
              ) : null}
            </>
          </div>
        </Dialog.Panel>
      </Dialog>
      <DrawerModal
        title={t('coupons.apply')}
        open={isSome(applyCouponState)}
        onClose={() => setApplyCouponState(null)}
      >
        {applyCouponState === 'qr' ? (
          <div className={styles.centered}>
            <p className={styles.hint}>{t('coupons.qr_hint')}</p>
            <QRCode
              className={styles.qrCode}
              value={coupon?.qrCode ?? ''}
              fgColor="var(--color-brand-2)"
            />{' '}
            {validTo ? (
              <p className={styles.hint}>
                {t('coupons.valid_to', { time: formatTime(validTo, locale) })}
              </p>
            ) : null}
            {coupon?.code ? (
              <p className={styles.hint}>
                {!couponCodeRevealed ? (
                  <Trans t={t} i18nKey="coupons.qr_hint_manual">
                    <button
                      className={styles.linkButton}
                      onClick={() => setCouponCodeRevealed(true)}
                    ></button>
                  </Trans>
                ) : (
                  <Trans
                    t={t}
                    i18nKey="coupons.qr_hint_code"
                    values={{
                      code: coupon?.code ?? '',
                    }}
                  ></Trans>
                )}
              </p>
            ) : null}
          </div>
        ) : null}
        {applyCouponState === 'code' ? (
          <div className={styles.centered}>
            <CopyArea className={styles.code}>{coupon?.code ?? ''}</CopyArea>
            <p className={styles.hint}>{t('coupons.code_hint')}</p>
            {validTo ? (
              <p className={styles.hint}>
                {t('coupons.valid_to', { time: formatTime(validTo, locale) })}
              </p>
            ) : null}
          </div>
        ) : null}
        {applyCouponState === 'select' ? (
          <ul>
            {coupon?.qrCode && coupon?.availability?.includes?.('kiosk') ? (
              <li className={styles.selectItem}>
                <Button
                  className={styles.selectButton}
                  variant="transparent"
                  onClick={() => setApplyCouponState('qr')}
                >
                  {t('coupons.apply_at_restaurant')}
                </Button>
              </li>
            ) : null}
            {coupon?.code && coupon?.availability?.includes?.('app') ? (
              <li className={styles.selectItem}>
                <Button
                  className={styles.selectButton}
                  variant="transparent"
                  onClick={() => setApplyCouponState('code')}
                >
                  {t('coupons.apply_in_app')}
                </Button>
              </li>
            ) : null}
          </ul>
        ) : null}
      </DrawerModal>
    </>
  );
};

export default CouponModal;
